import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApolloProvider } from '@apollo/client/react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { createRoot } from 'react-dom/client';
import ErrorBoundary from './core/components/ErrorBoundary';
import { client } from './main/client';
import App from './main/component/App';

const defaultTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: '#6366f1', // Vibrant indigo accent
            light: '#818cf8',
            dark: '#4f46e5',
        },
        secondary: {
            main: '#f59e0b', // Amber accent for CTAs
            light: '#fbbf24',
            dark: '#d97706',
        },
        background: {
            default: '#0f0f23', // Deep navy/charcoal
            paper: '#1a1a2e', // Slightly lighter for cards
        },
        text: {
            primary: '#ffffff',
            secondary: '#d1d5db',
        },
    },
    typography: {
        fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
        ].join(','),
        h1: {
            fontWeight: 900,
            letterSpacing: '-0.02em',
            textTransform: 'uppercase',
        },
        h2: {
            fontWeight: 900,
            letterSpacing: '-0.01em',
            textTransform: 'uppercase',
        },
        h3: {
            fontWeight: 800,
            letterSpacing: '-0.01em',
        },
        h4: {
            fontWeight: 700,
            letterSpacing: '0.02em',
        },
        h5: {
            fontWeight: 700,
            letterSpacing: '0.02em',
        },
        h6: {
            fontWeight: 700,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
        },
        button: {
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 0,
                    padding: '12px 24px',
                    border: '3px solid',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        transform: 'translate(-2px, -2px)',
                        boxShadow: '4px 4px 0px currentColor',
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    borderBottom: '4px solid #6366f1',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRight: '4px solid #6366f1',
                    backgroundColor: '#0f0f23',
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderLeft: '4px solid transparent',
                    transition: 'all 0.2s ease',
                    fontWeight: 600,
                    '&:hover': {
                        borderLeftColor: '#6366f1',
                        backgroundColor: '#1a1a2e',
                        transform: 'translateX(4px)',
                    },
                    '&.Mui-selected': {
                        borderLeftColor: '#f59e0b',
                        backgroundColor: '#1a1a2e',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 0,
                    border: '2px solid #374151',
                },
            },
        },
    },
});

createRoot(document.getElementById('app')!).render(
    <ErrorBoundary fallback={<div>Something went wrong.</div>}>
        <ApolloProvider client={client as any}>
            <ThemeProvider theme={defaultTheme}>
                <App />
            </ThemeProvider>
        </ApolloProvider>
    </ErrorBoundary>
);
